import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';

const BannerForm = ({ form, setForm, saving, onSave, onDisable }) => (
  <Card className="bg-slate-900 border-slate-800 max-w-3xl">
    <CardHeader>
      <CardTitle className="text-lg text-white">Banner settings</CardTitle>
    </CardHeader>
    <CardContent className="space-y-5">
      <div className="flex items-center gap-3">
        <Switch
          data-testid="banner-enabled-switch"
          checked={form.enabled}
          onCheckedChange={(v) => setForm({ ...form, enabled: v })}
        />
        <Label className="text-gray-300">
          {form.enabled ? 'Banner is enabled' : 'Banner is disabled'}
        </Label>
      </div>

      <div>
        <Label className="text-gray-300">Message</Label>
        <Textarea
          data-testid="banner-message-input"
          value={form.message}
          onChange={(e) => setForm({ ...form, message: e.target.value })}
          rows={2}
          placeholder="e.g. Closed Thursday 5/15 for a Shop Day at the Range!"
          className="bg-slate-800 border-slate-700 text-white"
        />
        <p className="text-xs text-gray-500 mt-1">Keep it short — one line looks best.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-gray-300">Show from (optional)</Label>
          <Input
            data-testid="banner-starts-at"
            type="date"
            value={form.starts_at}
            onChange={(e) => setForm({ ...form, starts_at: e.target.value })}
            className="bg-slate-800 border-slate-700 text-white"
          />
        </div>
        <div>
          <Label className="text-gray-300">Hide after (optional)</Label>
          <Input
            data-testid="banner-ends-at"
            type="date"
            value={form.ends_at}
            onChange={(e) => setForm({ ...form, ends_at: e.target.value })}
            className="bg-slate-800 border-slate-700 text-white"
          />
        </div>
      </div>

      <div>
        <Label className="text-gray-300">Where to show it</Label>
        <div className="grid grid-cols-2 gap-2 mt-1">
          {[
            { v: 'all_pages', label: 'All pages' },
            { v: 'homepage_only', label: 'Homepage only' },
          ].map((opt) => {
            const active = (form.scope || 'all_pages') === opt.v;
            return (
              <button
                key={opt.v}
                type="button"
                data-testid={`banner-scope-${opt.v}`}
                onClick={() => setForm({ ...form, scope: opt.v })}
                className={`rounded-md py-2 text-sm border-2 transition-all ${active ? 'border-red-500 bg-slate-950 text-white' : 'border-slate-800 bg-slate-950/40 text-gray-400 hover:border-slate-700'}`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-gray-300">Optional button label</Label>
          <Input
            data-testid="banner-link-text"
            value={form.link_text || ''}
            onChange={(e) => setForm({ ...form, link_text: e.target.value })}
            placeholder="Learn more"
            className="bg-slate-800 border-slate-700 text-white"
          />
        </div>
        <div>
          <Label className="text-gray-300">Optional button link</Label>
          <Input
            data-testid="banner-link-url"
            value={form.link_url || ''}
            onChange={(e) => setForm({ ...form, link_url: e.target.value })}
            placeholder="/gallery or https://…"
            className="bg-slate-800 border-slate-700 text-white font-mono text-sm"
          />
        </div>
      </div>

      <div className="rounded border border-slate-800 p-4 bg-slate-950/60">
        <p className="text-xs text-gray-500 mb-2">Preview</p>
        {form.message.trim() ? (
          <div className="rounded bg-gradient-to-r from-red-700 via-red-600 to-red-700 text-white px-4 py-2 text-sm font-medium">
            {form.message}
          </div>
        ) : (
          <p className="text-xs text-gray-600">Add a message to see the preview.</p>
        )}
      </div>

      <div className="flex items-center gap-3 pt-2">
        <Button
          data-testid="banner-save-button"
          onClick={onSave}
          disabled={saving || (form.enabled && !form.message.trim())}
          className="bg-red-600 hover:bg-red-700 text-white"
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
        {form.enabled && (
          <Button
            data-testid="banner-disable-button"
            variant="outline"
            onClick={onDisable}
            disabled={saving}
            className="border-slate-700 text-white hover:bg-slate-800"
          >
            Disable now
          </Button>
        )}
      </div>
    </CardContent>
  </Card>
);

export default BannerForm;
